/**
 * The simulation orchestrator.
 *
 * This is the only place that knows the order systems run in. It owns no game
 * rules itself - deliberately, so that it stays a schedule rather than becoming
 * the God Object the brief warns against.
 *
 * It has no rendering, DOM, timer or I/O dependency of any kind. That is what
 * lets the balance harness run a thousand games in a few seconds in plain Node,
 * and what would let a different renderer (including a native engine) drive the
 * exact same game.
 */
import { Rng } from './core/rng.js';
import { IdGenerator } from './core/ids.js';
import { EventBus, type DaySummary } from './core/events.js';
import type { GameState, Lead } from './state/GameState.js';
import { BALANCE } from './data/balance.js';
import { getLocation } from './data/world.js';

import { TimeSystem } from './systems/TimeSystem.js';
import { FinanceSystem } from './systems/FinanceSystem.js';
import { NeedsSystem } from './systems/NeedsSystem.js';
import { ExpenseSystem } from './systems/ExpenseSystem.js';
import { JobSystem } from './systems/JobSystem.js';
import { CustomerSystem } from './systems/CustomerSystem.js';
import { ReputationSystem } from './systems/ReputationSystem.js';
import { BusinessSystem } from './systems/BusinessSystem.js';
import { EmployeeSystem } from './systems/EmployeeSystem.js';
import { PropertySystem } from './systems/PropertySystem.js';
import { MilestoneSystem } from './systems/MilestoneSystem.js';
import { WeatherSystem, forDay } from './systems/WeatherSystem.js';
import { WorldSimulation } from './systems/WorldSimulation.js';

export interface SimulationOptions {
  seed?: number;
  /** Skip visible-NPC setup when running purely headless. */
  visibleNpcs?: number;
}

export class Simulation {
  readonly bus = new EventBus();
  readonly rng: Rng;
  readonly ids = new IdGenerator();

  readonly time = new TimeSystem();
  readonly finance: FinanceSystem;
  readonly needs: NeedsSystem;
  readonly expenses: ExpenseSystem;
  readonly jobs: JobSystem;
  readonly customers: CustomerSystem;
  readonly reputation: ReputationSystem;
  readonly business: BusinessSystem;
  readonly employees: EmployeeSystem;
  readonly property: PropertySystem;
  readonly milestones: MilestoneSystem;
  readonly weather: WeatherSystem;
  readonly world: WorldSimulation;

  state: GameState;

  /** Accumulates fractional minutes between frames so nothing is lost. */
  private minuteCarry = 0;
  private lastDaySummary: DaySummary | null = null;
  /** Snapshots taken at each day boundary so the summary reports the day. */
  private dayStartJobs = 0;
  private dayStartEmployeeJobs = 0;

  constructor(opts: SimulationOptions = {}) {
    const seed = opts.seed ?? 20260828;
    this.rng = new Rng(seed);

    this.finance = new FinanceSystem(this.bus, this.ids);
    this.needs = new NeedsSystem(this.bus);
    this.expenses = new ExpenseSystem(this.bus);
    this.jobs = new JobSystem(this.bus, this.rng, this.ids);
    this.customers = new CustomerSystem(this.bus, this.rng, this.ids);
    this.reputation = new ReputationSystem(this.bus);
    this.business = new BusinessSystem(this.bus, this.rng);
    this.employees = new EmployeeSystem(this.bus, this.rng, this.ids);
    this.property = new PropertySystem(this.bus);
    this.milestones = new MilestoneSystem(this.bus);
    this.weather = new WeatherSystem();
    this.world = new WorldSimulation(seed);

    this.state = this.createInitialState(seed);
    this.customers.seedCustomers(this.state);
    // 44, not 28, because the town is now 340 m across rather than 200.
    // The crowd is culled at 78 m, so the cost is set by how many people are
    // near the camera, not by the total - and the total is what decides
    // whether the far districts read as inhabited or as a film set. Keeping
    // 28 over twice the area would have made the expansion feel emptier than
    // what it replaced.
    this.world.initialise(seed, opts.visibleNpcs ?? 44);
    this.jobs.refreshDaily(this.state);
    // The game opens at 07:00, which is inside the courier window, so the board
    // can be populated immediately rather than waiting for the first tick.
    this.jobs.topUpCourierJobs(this.state);
    this.customers.generateDailyLeads(this.state);
  }

  private createInitialState(seed: number): GameState {
    const home = getLocation('home');
    return {
      version: 1,
      seed,
      rngState: this.rng.getState(),
      idState: {},
      time: {
        // Day 1, 07:00. Early enough that the first day is a real day.
        totalMinutes: 7 * 60,
        day: 1,
        dayOfWeek: 0,
        minuteOfDay: 7 * 60,
        week: 1,
        speed: 1,
        paused: false,
        sleeping: false,
      },
      player: {
        x: home.x + home.entryX,
        z: home.z + home.entryZ,
        facing: 0,
        needs: {
          hunger: { value: BALANCE.needs.startingHunger },
          energy: { value: BALANCE.needs.startingEnergy },
        },
        housingId: 'room_shared',
        vehicleId: null,
        vehicleParked: null,
        fuel: 0,
        reputation: BALANCE.reputation.start,
        activeWork: null,
        atLocationId: 'home',
      },
      finance: {
        cash: BALANCE.startingCash,
        bank: BALANCE.startingBank,
        debt: 0,
        transactions: [],
        todayEarned: 0,
        todaySpent: 0,
        lifetimeEarned: 0,
        lifetimeSpent: 0,
      },
      business: {
        registered: false,
        name: 'Unregistered',
        serviceLineId: 'yard_care',
        equipment: [],
        equipmentCondition: {},
        crewKits: 0,
        employees: [],
        lifetimeRevenue: 0,
        lifetimeWages: 0,
        lifetimeEquipmentSpend: 0,
      },
      customers: [],
      leads: [],
      courierJobs: [],
      nextCourierRefreshMinute: 0,
      shiftOffer: null,
      shiftsWorkedToday: 0,
      milestones: [],
      weather: forDay(seed, 1),
      lastRentWeekCharged: 0,
      lastPhoneWeekCharged: 0,
      lastWagesWeekPaid: 0,
      lastUpkeepWeekCharged: 0,
      lastInterestWeekCharged: 0,
      lastReputationDecayWeek: 0,
      statistics: {
        jobsCompleted: 0,
        employeeJobsCompleted: 0,
        couriersCompleted: 0,
        shiftsCompleted: 0,
        leadsExpired: 0,
        daysPlayed: 1,
        peakNetWorth: BALANCE.startingCash,
        rentMissed: 0,
      },
    };
  }

  // --- Driving the clock ---------------------------------------------------

  /**
   * Advance by real seconds. The client calls this every frame; the speed
   * multiplier and pause state are applied here so callers never have to.
   */
  tickRealSeconds(seconds: number): void {
    const s = this.state;
    if (s.time.paused && !s.time.sleeping) return;
    const speed = s.time.sleeping ? 60 : s.time.speed;
    this.minuteCarry += seconds * BALANCE.time.minutesPerRealSecond * speed;
    const whole = Math.floor(this.minuteCarry);
    if (whole <= 0) return;
    this.minuteCarry -= whole;
    this.advanceMinutes(whole);
  }

  /**
   * Advance by in-game minutes. Everything that must happen at most once per
   * day is driven off the day boundaries the TimeSystem reports, so a single
   * large jump (sleeping, or the balance harness stepping a whole day) behaves
   * identically to many small steps.
   */
  advanceMinutes(minutes: number): void {
    if (minutes <= 0) return;
    const s = this.state;

    // Work in chunks so a big jump still crosses each day boundary in order.
    let remaining = minutes;
    while (remaining > 0) {
      const untilMidnight = BALANCE.time.minutesPerDay - s.time.minuteOfDay;
      const step = Math.min(remaining, untilMidnight);
      remaining -= step;

      const working = s.player.activeWork !== null;
      this.needs.tick(s, step, working);

      if (working) {
        const finished = this.business.progressWork(s, step);
        if (finished) this.completeActiveWork(finished);
      }

      const { daysCrossed } = this.time.advance(s, step);
      if (daysCrossed > 0) {
        for (let i = 0; i < daysCrossed; i++) this.onDayBoundary();
      }
      // Upkeep runs on every step, including the one that crossed midnight:
      // the day boundary itself is outside the courier window, so the board is
      // only ever filled here, once the clock reaches opening time.
      this.jobs.expireCourierJobs(s);
      this.jobs.topUpCourierJobs(s);
      this.customers.expireLeads(s, this.reputation);
    }

    this.milestones.check(s, this.finance);
    s.rngState = this.rng.getState();
    s.idState = this.ids.getState();
  }

  private onDayBoundary(): void {
    const s = this.state;

    // Close out the day that just ended.
    const summary: DaySummary = {
      day: s.time.day - 1,
      earned: s.finance.todayEarned,
      spent: s.finance.todaySpent,
      net: s.finance.todayEarned - s.finance.todaySpent,
      jobsDone: s.statistics.jobsCompleted - this.dayStartJobs,
      employeeJobsDone: s.statistics.employeeJobsCompleted - this.dayStartEmployeeJobs,
      cashEnd: s.finance.cash,
      netWorthEnd: this.finance.netWorth(s),
      reputation: s.player.reputation,
    };
    this.lastDaySummary = summary;
    this.bus.emit({ type: 'day.ended', day: summary.day, summary });

    s.finance.todayEarned = 0;
    s.finance.todaySpent = 0;
    s.statistics.daysPlayed += 1;
    this.dayStartJobs = s.statistics.jobsCompleted;
    this.dayStartEmployeeJobs = s.statistics.employeeJobsCompleted;

    // Weekly bookkeeping first, so the player knows where they stand.
    this.expenses.applyWeekly(s, this.finance);
    this.expenses.applyDaily(s, this.finance);

    if (s.time.dayOfWeek === BALANCE.employees.payDayOfWeek) {
      this.employees.payWages(s, this.finance);
    }
    if (s.lastReputationDecayWeek < s.time.week) {
      s.lastReputationDecayWeek = s.time.week;
      this.reputation.applyWeeklyDecay(s);
    }

    // Then open the new day.
    this.weather.roll(s);
    this.customers.expireLeads(s, this.reputation);
    this.jobs.refreshDaily(s);
    this.employees.startDay(s);
    this.customers.generateContractLeads(s);
    this.generateLeadsForDay();

    // The crew works their day in a block; the player finds out this evening.
    this.employees.runWorkingDay(
      s,
      this.business,
      this.customers,
      this.finance,
      this.reputation,
    );

    this.bus.emit({
      type: 'day.started',
      day: s.time.day,
      dayOfWeek: this.time.dayName(s),
    });
  }

  private generateLeadsForDay(): void {
    const s = this.state;
    // Demand from the background economic agents modulates the base rate, so
    // the neighbourhood has a rhythm instead of emitting leads at a constant
    // trickle. `generateDailyLeads` already respects the open-lead cap.
    const demand = this.world.demandMultiplier(s);
    this.customers.generateDailyLeads(s, demand);
  }

  // --- Player actions ------------------------------------------------------

  /** Begin the accepted lead the player is standing at. */
  startYardWork(leadId: string): boolean {
    const lead = this.customers.getLead(this.state, leadId);
    if (!lead || !lead.accepted || lead.assignedEmployeeId) return false;
    const perf = this.needs.performanceMultiplier(this.state);
    const customer = this.customers.getCustomer(this.state, lead.customerId) ?? null;
    return this.business.startWork(this.state, lead, perf, customer) !== null;
  }

  private completeActiveWork(work: ReturnType<BusinessSystem['progressWork']>): void {
    if (!work) return;
    const s = this.state;
    const lead = this.customers.getLead(s, work.refId);
    const perf = this.needs.performanceMultiplier(s);
    const customer = lead
      ? s.customers.find((c) => c.id === lead.customerId) ?? null
      : null;
    const result = this.business.settleWork(
      s, work, perf, this.finance, this.reputation, customer,
    );
    if (lead) this.customers.completeLead(s, lead, result.quality);
  }

  /** Sleep until the wake hour. */
  sleep(): void {
    const s = this.state;
    if (s.player.activeWork) return;
    const minutes = this.time.minutesUntilHour(s, BALANCE.time.wakeHour);
    s.time.sleeping = true;
    this.advanceMinutes(minutes);
    s.time.sleeping = false;
  }

  /**
   * Move the player to a location, consuming the travel time. Used by the
   * headless harness and by fast-travel; the 3D client moves the avatar
   * continuously instead and only tells the sim where it ended up.
   */
  travelTo(locationId: string): number {
    const s = this.state;
    const from = s.player.atLocationId ?? 'home';
    const loc = getLocation(locationId);
    const distance = Math.hypot(
      getLocation(from).x + getLocation(from).entryX - (loc.x + loc.entryX),
      getLocation(from).z + getLocation(from).entryZ - (loc.z + loc.entryZ),
    );
    const minutes = this.property.travelMinutes(s, distance);
    s.player.atLocationId = locationId;
    s.player.x = loc.x + loc.entryX;
    s.player.z = loc.z + loc.entryZ;
    this.advanceMinutes(minutes);
    return minutes;
  }

  /** Used by the 3D client, which owns the avatar's position itself. */
  setPlayerPosition(x: number, z: number, facing: number, atLocationId: string | null): void {
    this.state.player.x = x;
    this.state.player.z = z;
    this.state.player.facing = facing;
    this.state.player.atLocationId = atLocationId;
  }

  /**
   * Re-evaluate milestones now.
   *
   * Milestones used to be checked only when the clock advanced, so one
   * satisfied purely by an action - a sale, a hire, a purchase - stayed
   * unrecognised until the next tick. Anything that mutates state calls this.
   */
  checkProgress(): void {
    this.milestones.check(this.state, this.finance);
  }

  getLastDaySummary(): DaySummary | null {
    return this.lastDaySummary;
  }

  /** Accepted leads that still need doing, nearest-payout-first for the UI. */
  openWork(): Lead[] {
    return this.state.leads.filter((l) => l.accepted && !l.assignedEmployeeId);
  }

  netWorth(): number {
    return this.finance.netWorth(this.state);
  }

  /** Restore a loaded state, re-seeding the deterministic cursors. */
  restore(state: GameState): void {
    this.state = state;
    this.rng.setState(state.rngState);
    this.ids.setState(state.idState);
    this.minuteCarry = 0;
    this.world.initialise(state.seed);
  }
}
